import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Package,
  Loader,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  ChevronDown,
  X,
  RefreshCw,
  Truck
} from 'lucide-react';
import { packingService } from '../../../../services/packingService';
import { shipmentLineService } from '../../../../services/shipmentLineService';
import {
  ShipmentDetailDto,
  ShipmentOrderDto,
  OrderLineDto,
  UpdateShipmentRequest,
  DeliveryMethodDto,
  PackZoneDto,
  LoadingZoneDto
} from '../../../../types/packing';
import { ShipmentLineDto, ShipmentLineRow } from '../../../../types/shipmentLine';
import { ShipmentLineEditor } from '../../shared/ShipmentLineEditor';
import { PackageConfirmationSection } from './PackageConfirmationSection';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PackingDetailViewProps {
  groupingId: string;
  onBack: () => void;
  onComplete?: () => void;
}

export const PackingDetailView: React.FC<PackingDetailViewProps> = ({
  groupingId,
  onBack,
  onComplete
}) => {
  const { t } = useTranslation();
  const [shipment, setShipment] = useState<ShipmentDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  const [orderLines, setOrderLines] = useState<OrderLineDto[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);

  // Dropdown data
  const [deliveryMethods, setDeliveryMethods] = useState<DeliveryMethodDto[]>([]);
  const [packZones, setPackZones] = useState<PackZoneDto[]>([]);
  const [loadingZones, setLoadingZones] = useState<LoadingZoneDto[]>([]);

  // Package confirmation state
  const [packageCheckoutRequired, setPackageCheckoutRequired] = useState(false);
  const [allPackagesConfirmed, setAllPackagesConfirmed] = useState(false);

  // Editable fields
  const [numOfPackages, setNumOfPackages] = useState<number>(0);
  const [numOfPallets, setNumOfPallets] = useState<number>(0);
  const [totalWeight, setTotalWeight] = useState<string>('');
  const [totalVolume, setTotalVolume] = useState<string>('');
  const [packageRemark, setPackageRemark] = useState<string>('');
  const [selectedDeliveryMethodNo, setSelectedDeliveryMethodNo] = useState<number | null>(null);
  const [selectedPackZoneID, setSelectedPackZoneID] = useState<string>('');
  const [selectedLoadingZoneID, setSelectedLoadingZoneID] = useState<number | null>(null);

  // ShipmentLine state per order (for kolli-dimensjoner)
  const [shipmentLinesMap, setShipmentLinesMap] = useState<Map<number, ShipmentLineDto[]>>(new Map());
  const [shipmentLinesChangesMap, setShipmentLinesChangesMap] = useState<Map<number, ShipmentLineRow[]>>(new Map());
  const [expandedShipmentLines, setExpandedShipmentLines] = useState<number | null>(null);
  const [loadingShipmentLines, setLoadingShipmentLines] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadShipment();
    loadDropdownData();
  }, [groupingId]);

  const loadDropdownData = async () => {
    try {
      const [methods, zones, lzones, checkoutRequired] = await Promise.all([
        packingService.getDeliveryMethods(),
        packingService.getPackZones(),
        packingService.getLoadingZones(),
        packingService.isPackageCheckoutRequired()
      ]);
      setDeliveryMethods(methods);
      setPackZones(zones);
      setLoadingZones(lzones);
      setPackageCheckoutRequired(checkoutRequired);
    } catch (err) {
      console.error('Error loading dropdown data:', err);
    }
  };

  // Callback when all packages are confirmed
  const handleAllPackagesConfirmed = useCallback(() => {
    setAllPackagesConfirmed(true);
  }, []);

  const loadShipment = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await packingService.getOrCreateShipment(groupingId);
      setShipment(data);

      // Initialize editable fields
      setNumOfPackages(data.numOfPackages || 0);
      setNumOfPallets(data.numOfPallets || 0);
      setTotalWeight(data.totalWeight?.toString() || '');
      setTotalVolume(data.totalVolume?.toString() || '');
      setPackageRemark(data.packageRemark || '');
      setSelectedDeliveryMethodNo(data.deliveryMethodNo);
      setSelectedPackZoneID(data.packZoneID || '');
      setSelectedLoadingZoneID(data.loadingZoneID ?? data.suggestedLoadingZoneID ?? null);
    } catch (err) {
      console.error('Error loading shipment:', err);
      setError(err instanceof Error ? err.message : t('warehouse.modules.packing.errors.loadShipmentFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!shipment) return;

    setSaving(true);
    try {
      const request: UpdateShipmentRequest = {
        shipmentID: shipment.shipmentID,
        numOfPackages,
        numOfPallets,
        totalWeight: totalWeight ? parseFloat(totalWeight) : null,
        totalVolume: totalVolume ? parseFloat(totalVolume) : null,
        packageRemark: packageRemark || null,
        deliveryMethodNo: selectedDeliveryMethodNo,
        packZoneID: selectedPackZoneID || null,
        loadingZoneID: selectedLoadingZoneID
      };

      const response = await packingService.updateShipment(request);

      if (response.success) {
        // Refresh data
        await loadShipment();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!shipment) return;

    // Validate
    if (numOfPackages <= 0) {
      setError(t('warehouse.modules.packing.validation.packagesRequired'));
      return;
    }

    // Check if package confirmation is required but not complete
    if (packageCheckoutRequired && !allPackagesConfirmed) {
      setError(t('warehouse.modules.packing.validation.confirmAllPackages'));
      return;
    }

    if (!window.confirm(t('warehouse.modules.packing.confirmFinalize'))) {
      return;
    }

    setSaving(true);
    try {
      // First save any pending changes
      await handleSave();

      // Save shipment lines for each order that has changes
      if (shipmentLinesChangesMap.size > 0) {
        console.log('Saving shipment lines for orders...');
        for (const [orderNo, lines] of Array.from(shipmentLinesChangesMap.entries())) {
          // Find the order to get pListId
          const order = shipment.orders.find(o => o.orderNo === orderNo);
          if (order && lines.length > 0) {
            await shipmentLineService.saveAll({
              pListID: order.pListID,
              orderNo: orderNo,
              lines: lines.map((line, index) => ({
                lineID: line.lineID || undefined,
                transitPackagingID: line.transitPackagingID,
                description1: line.description1,
                numOfPackages: line.numOfPackages,
                weight: line.weight,
                volume: line.volume,
                height: line.height,
                width: line.width,
                length: line.length,
                sorting: line.sorting ?? index
              }))
            });
            console.log(`Saved ${lines.length} shipment lines for order ${orderNo}`);
          }
        }
      }

      // Then finalize
      const response = await packingService.finalizeShipment(shipment.shipmentID);

      if (response.success) {
        onComplete?.();
        onBack();
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('warehouse.modules.packing.errors.finalizeFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleOrderClick = async (order: ShipmentOrderDto) => {
    if (expandedOrder === order.orderNo) {
      setExpandedOrder(null);
      setOrderLines([]);
      return;
    }

    setExpandedOrder(order.orderNo);
    setLoadingLines(true);

    try {
      const lines = await packingService.getOrderLines(order.orderNo);
      setOrderLines(lines);
    } catch (err) {
      console.error('Error loading order lines:', err);
    } finally {
      setLoadingLines(false);
    }
  };

  // Toggle shipment lines expansion for an order
  const handleShipmentLinesToggle = async (order: ShipmentOrderDto) => {
    const orderNo = order.orderNo;

    if (expandedShipmentLines === orderNo) {
      // Collapse
      setExpandedShipmentLines(null);
      return;
    }

    // Expand and load shipment lines if not already loaded
    setExpandedShipmentLines(orderNo);

    if (!shipmentLinesMap.has(orderNo)) {
      setLoadingShipmentLines(prev => new Set(prev).add(orderNo));
      try {
        const lines = await shipmentLineService.getByPListOrder(order.pListID, orderNo);
        setShipmentLinesMap(prev => {
          const newMap = new Map(prev);
          newMap.set(orderNo, lines);
          return newMap;
        });
      } catch (err) {
        console.error('Error loading shipment lines:', err);
      } finally {
        setLoadingShipmentLines(prev => {
          const newSet = new Set(prev);
          newSet.delete(orderNo);
          return newSet;
        });
      }
    }
  };

  // Handle changes to shipment lines for an order
  const handleShipmentLinesChange = (orderNo: number, pListId: number, lines: ShipmentLineRow[]) => {
    setShipmentLinesChangesMap(prev => {
      const newMap = new Map(prev);
      newMap.set(orderNo, lines);
      return newMap;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-2" />
          <p className="text-sm sm:text-base text-gray-600">{t('warehouse.modules.packing.loadingShipment')}</p>
        </div>
      </div>
    );
  }

  if (error && !shipment) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-red-900">{t('common.error')}</h3>
              <p className="text-sm text-red-700 break-words">{error}</p>
              <button
                onClick={onBack}
                className="mt-3 text-sm text-red-600 hover:text-red-700 min-h-[44px] inline-flex items-center"
              >
                {t('common.buttons.back')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!shipment) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <Truck className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <span className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {t('warehouse.modules.packing.shipmentOrder')}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <MediaCaptureButton
              entityType="Packing"
              entityKey={groupingId}
            />
            <button
              onClick={loadShipment}
              className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <RefreshCw className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-3 sm:mx-4 mt-3 sm:mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 flex-1 break-words">{error}</p>
            <button
              onClick={() => setError(null)}
              className="flex-shrink-0 min-h-[44px] min-w-[44px] -m-2 p-2 flex items-center justify-center hover:bg-red-100 rounded"
            >
              <X className="w-4 h-4 text-red-600" />
            </button>
          </div>
        </div>
      )}

      {/* Form Fields */}
      <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
        {/* Row 1: Antall kolli, Antall paller, Vekt, Volum, Merkes */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.numberOfPackages')}</label>
            <input
              type="number"
              value={numOfPackages}
              onChange={(e) => setNumOfPackages(parseInt(e.target.value) || 0)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.numberOfPallets')}</label>
            <input
              type="number"
              value={numOfPallets}
              onChange={(e) => setNumOfPallets(parseFloat(e.target.value) || 0)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.weight')}</label>
            <input
              type="text"
              value={totalWeight}
              onChange={(e) => setTotalWeight(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.volume')}</label>
            <input
              type="text"
              value={totalVolume}
              onChange={(e) => setTotalVolume(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.remark')}</label>
            <input
              type="text"
              value={packageRemark}
              onChange={(e) => setPackageRemark(e.target.value)}
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
            />
          </div>
        </div>

        {/* Row 2: Transportør, Lastesone, Dato, Navn */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.carrier')}</label>
            <select
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
              value={selectedDeliveryMethodNo ?? ''}
              onChange={(e) => setSelectedDeliveryMethodNo(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">{t('common.select')}</option>
              {deliveryMethods.map((method) => (
                <option key={method.deliveryMethodNo} value={method.deliveryMethodNo}>
                  {method.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('warehouse.modules.packing.fields.packZone')}</label>
            <select
              className="w-full px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px]"
              value={selectedLoadingZoneID ?? ''}
              onChange={(e) => setSelectedLoadingZoneID(e.target.value ? parseInt(e.target.value) : null)}
            >
              <option value="">{t('common.select')}</option>
              {loadingZones.map((zone) => (
                <option key={zone.loadingZoneID} value={zone.loadingZoneID}>
                  {zone.description}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.date')}</label>
            <input
              type="text"
              value={shipment.shipmentDate ? new Date(shipment.shipmentDate).toLocaleDateString('nb-NO') : ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.name')}</label>
            <input
              type="text"
              value={shipment.delCustomerName || ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
        </div>

        {/* Row 3: Adresse, Postnr, Poststed, Landkode */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.address')}</label>
            <input
              type="text"
              value={shipment.delAddress1 || ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.zipCode')}</label>
            <input
              type="text"
              value={shipment.delZipCode || ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">{t('common.fields.city')}</label>
            <input
              type="text"
              value={shipment.delCity || ''}
              readOnly
              className="w-full px-2 sm:px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 min-h-[44px]"
            />
          </div>
        </div>

        {/* Package Confirmation Section */}
        <PackageConfirmationSection
          groupingId={groupingId}
          shipmentId={shipment.shipmentID}
          onAllPackagesConfirmed={handleAllPackagesConfirmed}
          isRequired={packageCheckoutRequired}
        />

        {/* Order List */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {shipment.orders.length === 0 ? (
            <div className="p-6 sm:p-8 text-center text-gray-500">
              <Package className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">{t('warehouse.modules.packing.noOrdersInShipment')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {shipment.orders.map((order) => (
                <div key={`${order.orderNo}-${order.pListID}`}>
                  {/* Order row */}
                  <div className="flex items-center px-3 sm:px-4 py-3 hover:bg-gray-50 gap-2">
                    {/* Checkmark */}
                    <div className="w-6 sm:w-8 flex-shrink-0">
                      {order.isReadyForPacking && (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      )}
                    </div>

                    {/* Order info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {order.displayText}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => {/* TODO: Remove from shipment */}}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOrderClick(order)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                      >
                        <ChevronRight
                          className={`w-5 h-5 transition-transform ${
                            expandedOrder === order.orderNo ? 'rotate-90' : ''
                          }`}
                        />
                      </button>
                    </div>
                  </div>

                  {/* Expanded order lines */}
                  {expandedOrder === order.orderNo && (
                    <div className="bg-gray-50 px-3 sm:px-4 py-3 border-t border-gray-200">
                      {loadingLines ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                        </div>
                      ) : orderLines.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          {t('warehouse.modules.packing.noLinesFound')}
                        </p>
                      ) : (
                        <div className="overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4">
                          <table className="w-full text-sm min-w-[500px]">
                            <thead>
                              <tr className="text-xs text-gray-500 uppercase">
                                <th className="text-left py-1 px-1">{t('common.fields.itemNumber')}</th>
                                <th className="text-left py-1 px-1">{t('common.fields.description')}</th>
                                <th className="text-right py-1 px-1">{t('common.fields.quantity')}</th>
                                <th className="text-right py-1 px-1">{t('warehouse.modules.packing.fields.picked')}</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {orderLines.map((line) => (
                                <tr key={line.lineNo}>
                                  <td className="py-2 px-1 font-medium text-xs sm:text-sm">{line.itemNo}</td>
                                  <td className="py-2 px-1 text-gray-600 text-xs sm:text-sm">{line.itemDescription}</td>
                                  <td className="py-2 px-1 text-right text-xs sm:text-sm">{line.quantity}</td>
                                  <td className="py-2 px-1 text-right text-xs sm:text-sm">{line.quantityPicked}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Transport-detaljer (ShipmentLine) section */}
                  <div className="border-t border-gray-100">
                    <button
                      onClick={() => handleShipmentLinesToggle(order)}
                      className="w-full px-3 sm:px-4 py-2 flex items-center justify-between text-sm text-gray-600 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        <span>{t('warehouse.modules.packing.packageDetails')}</span>
                        {(shipmentLinesMap.get(order.orderNo)?.length || 0) > 0 && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            {shipmentLinesMap.get(order.orderNo)?.length || 0}
                          </span>
                        )}
                      </div>
                      {loadingShipmentLines.has(order.orderNo) ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : expandedShipmentLines === order.orderNo ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>

                    {expandedShipmentLines === order.orderNo && (
                      <div className="px-3 sm:px-4 pb-3">
                        <ShipmentLineEditor
                          pListId={order.pListID}
                          orderNo={order.orderNo}
                          initialLines={shipmentLinesMap.get(order.orderNo) || []}
                          onChange={(lines) => handleShipmentLinesChange(order.orderNo, order.pListID, lines)}
                          disabled={saving}
                          expanded={true}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-3 sm:pt-4">
          <button
            onClick={onBack}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors min-h-[44px] order-2 sm:order-1"
          >
            {t('common.buttons.back')}
          </button>
          <button
            onClick={handleFinalize}
            disabled={saving}
            className="flex-1 px-4 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center justify-center gap-2 min-h-[44px] order-1 sm:order-2"
          >
            {saving ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                <span>{t('common.saving')}</span>
              </>
            ) : (
              <span>{t('warehouse.modules.packing.finalizeShipment')}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
